import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useMessagesContext, WaitingForResponseType } from "@/contexts/MessagesContext";

// Rotating value-prop tips. NOTE: every tip that hard-coded the (wrong) literal
// "A elaboração de uma peça pode levar de 3 a 5 minutos." was removed — the
// accurate, type-aware estimate now lives in the pinned message below.
const loadingTips = [
  "Com o Assistente Lexter, suas peças jurídicas ficam prontas em minutos.",
  "Menos tempo com documentos, mais tempo com seus clientes.",
  "Reduza em até 70% o tempo para redigir suas petições com Lexter.",
  "Otimize seu escritório e tenha mais tempo para pensar estrategicamente.",
  "Dúvidas jurídicas resolvidas em segundos com a inteligência do Lexter.",
  "Domine mais áreas do Direito com o suporte da IA jurídica.",
  "Organize processos e documentos em um só lugar, de forma simples.",
  "Com Lexter, cada contrato é elaborado de forma ágil e precisa.",
  "Menos rotina, mais resultados: automatize suas tarefas jurídicas.",
  "Torne-se um advogado mais eficiente com o poder da IA.",
  "Tome decisões rápidas com acesso instantâneo a jurisprudência.",
  "Sua jornada jurídica, mais ágil e simplificada com Lexter.",
  "Dedique seu tempo ao que importa: o relacionamento com seus clientes.",
  "Tenha insights jurídicos avançados com um clique.",
  "Amplie sua capacidade de atendimento sem comprometer a qualidade.",
  "O futuro do Direito é agora: use IA para avançar no seu dia a dia.",
];

type SkillProfile = {
  // Expected wall time used ONLY to pace the pseudo-progress + stage timer.
  // These are perceived-speed pacing hints, not contractual SLAs.
  estSeconds: number;
  stages: string[];
  pinnedMessage: string;
};

const DEFAULT_STAGES = ["Analisando o contexto", "Elaborando a resposta", "Finalizando"];

// Long-form drafting flow (petitions / motions / contracts): the named stages.
// The estimate is a single accurate range (was wrongly a fixed "3 a 5 minutos").
const DRAFTING_STAGES = [
  "Analisando o contexto",
  "Pesquisando jurisprudência",
  "Elaborando os argumentos",
  "Redigindo a peça",
  "Formatando o documento",
];

const SKILL_PROFILES: Record<WaitingForResponseType, SkillProfile> = {
  [WaitingForResponseType.GENERIC]: {
    estSeconds: 30,
    stages: DEFAULT_STAGES,
    pinnedMessage:
      "Estamos executando sua solicitação. Isso pode levar alguns instantes — você não precisa esperar nesta tela.",
  },
  [WaitingForResponseType.INTERACTION_WITH_CHAT]: {
    estSeconds: 30,
    stages: ["Analisando o contexto", "Elaborando a resposta", "Finalizando"],
    pinnedMessage: "Já estou trabalhando na sua solicitação. Em geral leva menos de um minuto.",
  },
  [WaitingForResponseType.LEGAL_QUESTION]: {
    estSeconds: 45,
    stages: ["Analisando a dúvida", "Pesquisando fundamentos", "Elaborando a resposta", "Finalizando"],
    pinnedMessage:
      "Estou pesquisando e elaborando a resposta para a sua dúvida jurídica. Geralmente leva cerca de 1 minuto.",
  },
  [WaitingForResponseType.CREATE_SUMMARY]: {
    estSeconds: 90,
    stages: ["Lendo o documento", "Identificando os pontos-chave", "Elaborando o resumo", "Finalizando"],
    pinnedMessage:
      "Estou lendo o documento e elaborando o resumo. Isso pode levar de 1 a 2 minutos, dependendo do tamanho.",
  },
  [WaitingForResponseType.DOCUMENT_UPLOAD]: {
    estSeconds: 20,
    stages: ["Enviando o documento", "Processando o arquivo", "Finalizando"],
    pinnedMessage: "Fazendo o upload e o processamento do seu documento. Isso leva apenas alguns instantes.",
  },
  [WaitingForResponseType.CONTRACT]: {
    estSeconds: 150,
    stages: DRAFTING_STAGES,
    pinnedMessage:
      "Estou elaborando o contrato. Esse processo costuma levar de 2 a 3 minutos. Ele será carregado automaticamente quando estiver pronto.",
  },
  [WaitingForResponseType.CREATE_DOCUMENT]: {
    estSeconds: 150,
    stages: DRAFTING_STAGES,
    pinnedMessage:
      "Estou elaborando o documento. Esse processo costuma levar de 2 a 3 minutos. Ele será carregado automaticamente quando estiver pronto.",
  },
  [WaitingForResponseType.CREATE_MOTION]: {
    estSeconds: 180,
    stages: DRAFTING_STAGES,
    pinnedMessage:
      "Estou elaborando a peça solicitada. Esse processo costuma levar de 2 a 4 minutos. Ela será carregada automaticamente quando estiver pronta.",
  },
};

const formatElapsed = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const RunningSkill = () => {
  const { palette } = useTheme();
  const { waitingForResponse } = useMessagesContext();

  // Single source of truth for pacing. Re-resolved whenever the waiting type
  // changes (e.g. a flow that transitions DOCUMENT_UPLOAD -> CREATE_MOTION).
  const profile = useMemo<SkillProfile>(() => {
    const type = waitingForResponse?.type;
    if (type && SKILL_PROFILES[type]) return SKILL_PROFILES[type];
    return SKILL_PROFILES[WaitingForResponseType.GENERIC];
  }, [waitingForResponse?.type]);

  const [tipIndex, setTipIndex] = useState(0);
  const [stageIndex, setStageIndex] = useState(0);
  const [progress, setProgress] = useState(0); // 0..~85 while running
  const [elapsed, setElapsed] = useState(0); // whole seconds

  // Reset pacing when the profile (skill type) changes so a transition restarts
  // the staged experience instead of jumping mid-way.
  const startRef = useRef<number>(Date.now());
  useEffect(() => {
    startRef.current = Date.now();
    setStageIndex(0);
    setProgress(0);
    setElapsed(0);
  }, [profile]);

  // Rotating tips — purely cosmetic, fixed cadence.
  useEffect(() => {
    const id = window.setInterval(() => {
      setTipIndex((prev) => (prev + 1) % loadingTips.length);
    }, 8000);
    return () => window.clearInterval(id);
  }, []);

  // Pacing loop: eased pseudo-determinate progress + stage advance + elapsed.
  // Eases toward ~85% over estSeconds (never reaches 100 while running); the
  // jump to 100% happens implicitly on unmount when the result lands.
  useEffect(() => {
    const ASYMPTOTE = 85;
    const id = window.setInterval(() => {
      const elapsedSec = (Date.now() - startRef.current) / 1000;
      setElapsed(Math.floor(elapsedSec));

      // Ease-out toward ASYMPTOTE; never completes while running.
      const tau = Math.max(profile.estSeconds * 0.55, 1);
      const eased = (1 - Math.exp(-elapsedSec / tau)) * ASYMPTOTE;
      setProgress((prev) => Math.max(prev, Math.min(eased, ASYMPTOTE)));

      // Stage advance proportional to estSeconds across the stage list. Final
      // stage is reached at ~90% of estSeconds and held until completion.
      const perStage = (profile.estSeconds * 0.9) / profile.stages.length;
      const idx = Math.min(Math.floor(elapsedSec / Math.max(perStage, 1)), profile.stages.length - 1);
      setStageIndex(idx);
    }, 250);
    return () => window.clearInterval(id);
  }, [profile]);

  const currentStage = profile.stages[stageIndex] ?? profile.stages[profile.stages.length - 1];

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="24px"
      width="100%"
      px={2}
      role="status"
      aria-live="polite"
      component={motion.div}
      key="running_skill"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ delay: 0.3 }}
    >
      {/* Stage + elapsed */}
      <Box display="flex" flexDirection="column" alignItems="center" gap="8px" width="100%" maxWidth="360px">
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <AnimatePresence mode="wait">
            <Typography
              key={currentStage}
              component={motion.span}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              variant="body3"
              sx={{ fontWeight: 700, color: palette.primary.main }}
            >
              {currentStage}…
            </Typography>
          </AnimatePresence>
          <Typography
            variant="body2"
            aria-hidden
            sx={{ color: palette.text.secondary, fontVariantNumeric: "tabular-nums" }}
          >
            {formatElapsed(elapsed)}
          </Typography>
        </Box>

        {/* Pseudo-determinate progress bar */}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            width: "100%",
            height: 6,
            borderRadius: 3,
            backgroundColor: palette.orange?.light[20],
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              backgroundColor: palette.primary.main,
              transition: "transform 0.4s ease-out",
            },
          }}
        />
      </Box>

      {/* Pinned, accurate estimate for this skill type */}
      <Typography variant="multiLineBody" textAlign="center" maxWidth="360px" sx={{ color: palette.text.primary }}>
        {profile.pinnedMessage}
      </Typography>

      {/* Rotating value-prop tip */}
      <AnimatePresence mode="wait">
        <Typography
          key={tipIndex}
          component={motion.p}
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          variant="body2"
          textAlign="center"
          maxWidth="320px"
          sx={{ color: palette.text.secondary, m: 0 }}
        >
          {loadingTips[tipIndex]}
        </Typography>
      </AnimatePresence>
    </Box>
  );
};
